import { i18n } from '../i18n/i18n';
import { utils } from '../utils/utils';
import { EditorTag } from '../types/editor_tag';
import { DataType } from '../types/data_type';
import { MetaEntity, MetaEntityAttr } from './meta_entity';
import { ValueEditor } from './value_editor';
/**
 * Represents a data model
 */
var MetaData = /** @class */ (function () {
    /** The default constructor. */
    function MetaData() {
        this.mainEntity = null;
        this.id = '__none';
        this.name = 'Empty model';
        this.rootEntity = this.createEntity();
        this.displayFormats = new Map();
    }
    /**
     * Gets the main entity of model
     * @return The main entity.
     */
    MetaData.prototype.getMainEntity = function () {
        return this.mainEntity;
    };
    MetaData.prototype.createEntity = function (parent) {
        return new MetaEntity(parent);
    };
    MetaData.prototype.createEntityAttr = function (parent) {
        return new MetaEntityAttr(parent);
    };
    MetaData.prototype.createValueEditor = function () {
        return new ValueEditor();
    };
    /**
     * Loads data model from JSON.
     * @param stringJson The JSON string.
     */
    MetaData.prototype.loadFromJSON = function (stringJson) {
        var model = JSON.parse(stringJson);
        this.loadFromData(model);
    };
    /**
     * Loads data model from its JSON representation object.
     * @param data The JSON representation object.
     */
    MetaData.prototype.loadFromData = function (data) {
        this.id = data.id;
        this.name = data.name;
        this.version = data.vers;
        //Editors
        this.editors = new Array();
        if (data.editors) {
            for (var i = 0; i < data.editors.length; i++) {
                var newEditor = this.createValueEditor();
                newEditor.loadFromData(data.editors[i]);
                this.editors.push(newEditor);
            }
        }
        //rootEntity
        this.rootEntity.loadFromData(this, data.entroot);
        //DataFormats
        this.displayFormats = new Map();
        if (data.displayFormats) {
            for (var dtypeStr in data.displayFormats) {
                var dtype = DataType[dtypeStr];
                var formats = data.displayFormats[dtypeStr] || new Array();
                this.displayFormats.set(dtype, formats);
            }
        }
    };
    /**
     * Gets the display formats.
     * @returns The display formats.
     */
    MetaData.prototype.getDisplayFormats = function () {
        return this.displayFormats;
    };
    /**
     * Gets the display formats for type
     * @param type The type
     * @returns An array of display formats
     */
    MetaData.prototype.getDisplayFormatsForType = function (type) {
        if (this.displayFormats.has(type)) {
            return this.displayFormats.get(type);
        }
        return [];
    };
    /**
     * Gets the default display format for the provided type
     * @param type The type
     * @returns The default type format or null
     */
    MetaData.prototype.getDefaultFormat = function (type) {
        if (this.displayFormats.has(type)) {
            return this.displayFormats.get(type).filter(function (f) { return f.isdef; })[0];
        }
        return null;
    };
    /**
     * Sets data to data model.
     * @param model Its JSON representation object or JSON string.
     */
    MetaData.prototype.setData = function (model) {
        if (typeof model === 'string') {
            this.loadFromJSON(model);
        }
        else {
            this.loadFromData(model);
        }
    };
    /**
     * Checks wether the data model is empty.
     * @returns `true` if the data model is empty, otherwise `false`.
     */
    MetaData.prototype.isEmpty = function () {
        return this.rootEntity.subEntities.length === 0 && this.rootEntity.attributes.length === 0;
    };
    /**
     * Gets ID of the data model.
     * @returns The ID.
     */
    MetaData.prototype.getId = function () {
        return this.id;
    };
    /**
     * Gets name of the data model.
     * @returns The name.
     */
    MetaData.prototype.getName = function () {
        return this.name;
    };
    /**
     * Gets root entity of the data model.
     * @returns The root entity.
     */
    MetaData.prototype.getRootEntity = function () {
        return this.rootEntity;
    };
    /**
     * Finds editor by its ID.
     * @param editorId The editor ID.
     * @returns The value editor or `null`.
     */
    MetaData.prototype.getEditorById = function (editorId) {
        for (var _i = 0, _a = this.editors; _i < _a.length; _i++) {
            var editor = _a[_i];
            if (editor.id === editorId) {
                return editor;
            }
        }
        return null;
    };
    /**
     * Gets entity attribute by its ID.
     * This function runs through all attributes inside specified model (it's root entity and all its sub-entities).
     * @param attrId The attribute ID.
     * @returns The attribute or `null`.
     */
    MetaData.prototype.getAttributeById = function (attrId) {
        var attr = this.getEntityAttrById(this.getRootEntity(), attrId);
        if (!attr) {
            return null;
        }
        return attr;
    };
    /**
     * Checks wether attribute contains such property.
     * @param attrId The attribute ID.
     * @param propName The property name.
     * @returns `true` if the attribute contains the property, otherwise `false`.
     */
    MetaData.prototype.checkAttrProperty = function (attrId, propName) {
        var attribute = this.getAttributeById(attrId);
        if (attribute) {
            if (typeof attribute[propName] === 'undefined') {
                throw 'No such property: ' + propName;
            }
            if (attribute[propName]) {
                return true;
            }
            else if (attribute.lookupAttr) {
                attrId = attribute.lookupAttr;
                attribute = this.getAttributeById(attrId);
                return attribute && attribute[propName];
            }
            else {
                return false;
            }
        }
        else
            return false;
    };
    /**
     * Gets entity attribute by its ID.
     * This function runs through all attributes inside specified entity and all its sub-entities.
     * @param entity
     * @param attrId
     * @returns The attribute or `null`.
     */
    MetaData.prototype.getEntityAttrById = function (entity, attrId) {
        var idx;
        if (entity.attributes) {
            var attrCount = entity.attributes.length;
            for (idx = 0; idx < attrCount; idx++) {
                if (entity.attributes[idx].id == attrId) {
                    return entity.attributes[idx];
                }
            }
        }
        var res;
        if (entity.subEntities) {
            var subEntityCount = entity.subEntities.length;
            for (idx = 0; idx < subEntityCount; idx++) {
                res = this.getEntityAttrById(entity.subEntities[idx], attrId);
                if (res)
                    return res;
            }
        }
        return null;
    };
    MetaData.prototype.listByEntityWithFilter = function (entity, filterFunc) {
        var result = new Array();
        var caption;
        var ent = null;
        if (entity.subEntities) {
            var subEntityCount = entity.subEntities.length;
            for (var entIdx = 0; entIdx < subEntityCount; entIdx++) {
                ent = entity.subEntities[entIdx];
                if (!filterFunc || filterFunc(ent, null)) {
                    caption = i18n.getText('Entities', ent.name);
                    if (!caption) {
                        caption = ent.caption;
                    }
                    var newEnt = utils.assign(this.createEntity(), { id: ent.name, text: caption, items: [], isEntity: true });
                    newEnt.items = this.listByEntityWithFilter(ent, filterFunc);
                    if (newEnt.items.length > 0)
                        result.push(newEnt);
                }
            }
        }
        var attr = null;
        if (entity.attributes) {
            var attrCount = entity.attributes.length;
            for (var attrIdx = 0; attrIdx < attrCount; attrIdx++) {
                attr = entity.attributes[attrIdx];
                if (!filterFunc || filterFunc(entity, attr)) {
                    caption = i18n.getText('Attributes', attr.id);
                    if (!caption)
                        caption = attr.caption;
                    var newEnt = utils.assign(this.createEntity(), { id: attr.id, text: caption, dataType: attr.dataType });
                    result.push(newEnt);
                }
            }
        }
        return result;
    };
    MetaData.prototype.listByEntity = function (entity, opts, filterFunc) {
        opts = opts || {};
        var resultEntities = [];
        var resultAttributes = [];
        var caption;
        var ent = null;
        if (entity.subEntities) {
            var subEntityCount = entity.subEntities.length;
            for (var entIdx = 0; entIdx < subEntityCount; entIdx++) {
                ent = entity.subEntities[entIdx];
                if (!filterFunc || filterFunc(ent, null)) {
                    caption = i18n.getText('Entities', ent.name) || ent.caption;
                    var newEnt = utils.assign(this.createEntity(), {
                        id: ent.name,
                        text: caption,
                        items: [],
                        isEntity: true,
                        description: ent.description
                    });
                    var newOpts = utils.assign({}, opts);
                    newOpts.includeRootData = false;
                    newEnt.items = this.listByEntity(ent, newOpts, filterFunc);
                    if (newEnt.items.length > 0) {
                        resultEntities.push(newEnt);
                    }
                }
            }
        }
        var attr = null;
        if (entity.attributes) {
            var attrCount = entity.attributes.length;
            for (var attrIdx = 0; attrIdx < attrCount; attrIdx++) {
                attr = entity.attributes[attrIdx];
                if (!filterFunc || filterFunc(entity, attr)) {
                    caption = i18n.getText('Attributes', attr.id) || attr.caption;
                    resultAttributes.push(utils.assign(this.createEntityAttr(entity), {
                        id: attr.id, text: caption,
                        dataType: attr.dataType, lookupAttr: attr.lookupAttr,
                        description: attr.description
                    }));
                }
            }
        }
        var sortCheck = function (a, b) {
            if (a.text.toLowerCase() == b.text.toLowerCase()) {
                return 0;
            }
            if (a.text.toLowerCase() > b.text.toLowerCase()) {
                return 1;
            }
            return -1;
        };
        if (opts.sortEntities) {
            resultEntities.sort(sortCheck);
            resultAttributes.sort(sortCheck);
        }
        var result;
        if (!opts.attrPlacement || opts.attrPlacement == 0) {
            result = resultEntities.concat(resultAttributes);
        }
        else {
            result = resultAttributes.concat(resultEntities);
        }
        if (opts.attrPlacement == 2) {
            result.sort(sortCheck);
        }
        if (opts.includeRootData) {
            caption = i18n.getText('Entities', entity.name);
            if (!caption)
                caption = entity.caption;
            return { id: entity.name, text: caption, items: result };
        }
        else {
            return result;
        }
    };
    /**
     * Clears data model.
     */
    MetaData.prototype.clear = function () {
        this.rootEntity = this.createEntity();
        this.editors = [];
        this.version = '';
    };
    /**
     * Add default value editors.
     */
    MetaData.prototype.addDefaultValueEditors = function () {
        var ve;
        ve = this.addOrUpdateValueEditor('_DTE', EditorTag.Edit, DataType.String);
        ve.defValue = '';
        this.addOrUpdateValueEditor('_DPDE', EditorTag.DateTime, DataType.DateTime);
        this.addOrUpdateValueEditor('_DPTE', EditorTag.DateTime, DataType.DateTime);
    };
    /**
    * Add or update a value editor.
    * @param id The id.
    * @param tag The tag.
    * @param resType The result type.
    * @returns The value editor.
    */
    MetaData.prototype.addOrUpdateValueEditor = function (id, tag, resType) {
        var ve = utils.findItemById(this.editors, id);
        if (!ve) {
            ve = this.createValueEditor();
            ve.id = id;
            this.editors.push(ve);
        }
        ve.tag = tag;
        ve.resType = resType;
        return ve;
    };
    /**
     * Gets entities tree.
     * @param opts The options.
     * @param filterFunc The filter function.
     * Takes two parameters, Entity and EntityAttr (second parameter will be null for entities), and returns boolean (true if the corresponding entity or attribute).
     * @returns The tree of the entities and their attributes according to options and the filter function
     */
    MetaData.prototype.getEntitiesTree = function (opts, filterFunc) {
        return this.listByEntity(this.getRootEntity(), opts, filterFunc);
    };
    /**
     * Gets entities tree due to filter.
     * @param filterFunc The filter function.
     * Takes two parameters, Entity and EntityAttr (second parameter will be null for entities), and returns boolean (true if the corresponding entity or attribute).
     * @returns The tree of the entities and their attributes according to the filter function
     */
    MetaData.prototype.getEntitiesTreeWithFilter = function (filterFunc) {
        return this.listByEntityWithFilter(this.getRootEntity(), filterFunc);
    };
    /**
     * Finds full entity path by attribute
     * @param attrId The attribute id.
     * @param sep The separator.
     * @returns The path.
     */
    MetaData.prototype.getFullEntityPathByAttr = function (attrId, sep) {
        sep = sep || ' ';
        return this.getEntityPathByAttr(this.getRootEntity(), attrId, sep, true);
    };
    /**
    * Finds entity path by attribute
    * @param entity The entity.
    * @param attrId The attribute id.
    * @param sep The separator.
    * @param root The root option.
    * @returns The path.
    */
    MetaData.prototype.getEntityPathByAttr = function (entity, attrId, sep, root) {
        if (!entity)
            return '';
        sep = sep || ' ';
        var entityCaption = '';
        if (entity.caption && !root) {
            var entityText = i18n.getText('Entities', entity.caption);
            entityCaption = entityText ? entityText : entity.caption;
        }
        if (entity.attributes) {
            var attrCount = entity.attributes.length;
            for (var i = 0; i < attrCount; i++) {
                if (entity.attributes[i].id == attrId) {
                    return entityCaption;
                }
            }
        }
        if (entity.subEntities) {
            var subEntityCount = entity.subEntities.length;
            for (var i = 0; i < subEntityCount; i++) {
                var ent = entity.subEntities[i];
                var res = this.getEntityPathByAttr(ent, attrId, sep, false);
                if (res !== '') {
                    if (entityCaption !== '')
                        res = entityCaption + sep + res;
                    return res;
                }
            }
        }
        return '';
    };
    /**
     * Gets the attribute text.
     * @param attr The attribute.
     * @param format The format.
     * @returns Formatted text.
     */
    MetaData.prototype.getAttributeText = function (attr, format) {
        var attrText = i18n.getText('Attributes', attr.id);
        if (!attrText) {
            attrText = attr.caption;
        }
        if (!format) {
            return attrText;
        }
        var result = '';
        var entityPath = this.getFullEntityPathByAttr(attr.id, ' ');
        if (entityPath) {
            result = format.replace(new RegExp('{attr}', 'g'), attrText);
            result = result.replace(new RegExp('{entity}', 'g'), entityPath);
        }
        else {
            result = attrText;
        }
        return result.trim();
    };
    /**
     * Scans model's entity tree and calls the callback functions for each attribute and entity.
     * @param processAttribute The callback function which is called for each attribute in model's entity tree.
     * The processed attribute is passed in the first function parameter.
     * @param processEntity The callback function which is called for each entity in tree.
     * The processed entity is passed in the first function parameter.
     */
    MetaData.prototype.runThroughEntities = function (processAttribute, processEntity) {
        this.getRootEntity().scan(processAttribute, processEntity);
    };
    /**
     * Finds first attribute by filter.
     * @param filterFunc The filter function. Takes EntityAttr object in parameter and returns boolean
     */
    MetaData.prototype.getFirstAttributeByFilter = function (filterFunc) {
        var res = null;
        this.runThroughEntities(function (attr, opts) {
            if (filterFunc(attr)) {
                opts.stop = true;
                res = attr;
            }
        }, null);
        return res;
    };
    return MetaData;
}());
export { MetaData };
//# sourceMappingURL=meta_data.js.map